import * as React from 'react';
import { type Breakpoints, breakpointsSortedByWidth, getBreakpointByWidth } from './breakpoints';
import { guessScreenSize } from './guess-screen-size';
import { DEVICE_WIDTH_COOKIE_NAME, useSetDeviceWidth } from './device-width';
import { getRequestContext } from './server/request-context';
import { getCookie } from 'hono/cookie';

export type MatchDevice = {
  isMobile: boolean;
  isTablet: boolean;
  isLaptop: boolean;
  isDesktop: boolean;
  [key: string]: boolean;
};

const BreakpointContext = React.createContext<keyof Breakpoints>('mobile');

export function useBreakpoint(): keyof Breakpoints {
  const context = React.useContext(BreakpointContext);

  if (typeof context === 'undefined') {
    throw new Error('Breakpoint was not provided');
  }

  return context;
}

export function useMatchDevice() {
  const breakpoint = useBreakpoint();
  return {
    isMobile: breakpoint === 'mobile',
    isTablet: breakpoint === 'tablet',
    isLaptop: breakpoint === 'laptop',
    isDesktop: breakpoint === 'desktop',
  };
}

export function useMatchMobile() {
  const { isMobile } = useMatchDevice();
  return isMobile;
}

export function useMatchTablet() {
  const { isTablet } = useMatchDevice();
  return isTablet;
}

export function useMatchLaptop() {
  const { isLaptop } = useMatchDevice();
  return isLaptop;
}

export function useMatchDesktop() {
  const { isDesktop } = useMatchDevice();
  return isDesktop;
}

export function useMatchSmallViewport() {
  const { isMobile, isTablet } = useMatchDevice();
  return isMobile || isTablet;
}

export function useMatchLargeViewport() {
  const { isLaptop, isDesktop } = useMatchDevice();
  return isLaptop || isDesktop;
}

function deviceWidth() {
  const c = getRequestContext();
  return Number(getCookie(c.honoContext, DEVICE_WIDTH_COOKIE_NAME) || guessScreenSize(c.honoContext).width);
}

export function getDeviceWidth() {
  return __BROWSER__ ? window.innerWidth : deviceWidth();
}

export function getBreakpoint() {
  const width = getDeviceWidth();
  return getBreakpointByWidth(width);
}

export const BreakpointProvider = React.memo(({ children }: React.PropsWithChildren<{}>) => {
  const [breakpoint, setBreakpoint] = React.useState<keyof Breakpoints>(getBreakpoint());
  useSetDeviceWidth();
  React.useEffect(() => {
    let index = 0;
    for (const [key, width] of breakpointsSortedByWidth) {
      let mediaQuery: string;
      // first breakpoint
      if (index === 0) {
        mediaQuery = `(max-width: ${width}px)`;
      }
      // last breakpoint
      else if (index === breakpointsSortedByWidth.length - 1) {
        mediaQuery = `(min-width: ${breakpointsSortedByWidth[index - 1][1] + 1}px)`;
      } else {
        mediaQuery = `(min-width: ${breakpointsSortedByWidth[index - 1][1] + 1}px)  and (max-width: ${width}px)`;
      }

      window.matchMedia(mediaQuery).onchange = e => {
        if (e.matches) {
          setBreakpoint(key);
        }
      };

      index++;
    }
  }, []);

  return <BreakpointContext.Provider value={breakpoint}>{children}</BreakpointContext.Provider>;
});
